import {
  memo, useCallback, useContext, useMemo, useState, useLayoutEffect,
  type ReactNode, type FunctionComponent
} from 'react';
import { useIntl } from 'react-intl';
// Material UI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
// Skillmore UI Components
import { fontWeightMedium } from '@empathco/ui-components/src/styles/themeOptions';
import { mapChunks } from '@empathco/ui-components/src/helpers/intl';
import StandardLink from '@empathco/ui-components/src/elements/StandardLink';
import ActionSucceededMessage from '@empathco/ui-components/src/elements/ActionSucceededMessage';
// local imports
import { type CustomerTicker } from '../config/customer';
import { openMessage, setOpenMessage } from '../helpers/storage';
import { GlobalContext } from '../context/global';
import useTicker from '../hooks/useTicker';
// SCSS imports
import { tckr, warn, ln } from './AppBarTicker.module.scss';

type AppBarTickerProps = {
  readonly tickers?: CustomerTicker[];
  readonly atJobScreen?: boolean;
}

const AppBarTicker: FunctionComponent<AppBarTickerProps> = ({
  tickers,
  atJobScreen = false
}) => {
  const { formatMessage } = useIntl();

  const { user: { data: user } } = useContext(GlobalContext);
  const { code, first_name: name } = user || {};
  const { id, ticker, popup, is_warning } = useTicker(tickers, atJobScreen);
  const tickerId = `ticker${id}|`;

  const shouldOpen = openMessage(tickerId, code);
  const [open, setOpen] = useState(false);
  useLayoutEffect(() => {
    if (shouldOpen) setOpen(true);
  }, [shouldOpen]);

  const handlePopupOpen = useCallback(() => setOpen(true), []);
  const handlePopupClose = useCallback(() => {
    setOpen(false);
    setOpenMessage(tickerId, code);
  }, [code, tickerId]);

  const values = useMemo(() => popup ? {
    name,
    // eslint-disable-next-line react/no-unstable-nested-components
    line: (chunks?: ReactNode | null): ReactNode => (
      <Typography
          align="center"
          variant="inherit"
          paragraph
          className={ln}
      >
        {mapChunks(chunks)}
      </Typography>
    ),
    // eslint-disable-next-line react/no-unstable-nested-components
    bold: (chunks?: ReactNode | null): ReactNode => (
      <Box fontWeight={fontWeightMedium} component="span">
        {mapChunks(chunks)}
      </Box>
    ),
    br: <br/>,
    link: popup.link ? (chunks?: ReactNode | null): ReactNode => (
      <StandardLink href={formatMessage({ id: popup.link as string, defaultMessage: popup.link as string })}>
        {mapChunks(chunks)}
      </StandardLink>
    ) : undefined,
    inline_link: popup.inline_link ? (chunks?: ReactNode | null): ReactNode => (
      <StandardLink href={formatMessage({ id: popup.inline_link as string, defaultMessage: popup.inline_link as string })}>
        {mapChunks(chunks)}
      </StandardLink>
    ) : undefined
  } : undefined, [name, popup, formatMessage]);

  return ticker || popup ? (
    <>
      {ticker ? (
        <Box
            p={1.75}
            textAlign="center"
            className={is_warning ? warn : tckr}
        >
          <StandardLink
              active
              variant="h4"
              text={ticker.text}
              link={ticker.link}
              onClick={ticker.link ? undefined : handlePopupOpen}
          />
        </Box>
      ) : undefined}
      {popup ? (
        <ActionSucceededMessage
            open={open}
            onOK={handlePopupClose}
            maxWidth="md"
            withClose
            disableTypography
            title={popup.title}
            message={popup.text}
            button={popup.button}
            link={popup.link}
            values={values}
        />
      ) : undefined}
    </>
  ) : null;
};

export default memo(AppBarTicker);
